/**
 * Which of an organisation's two logos to draw.
 *
 * Most school logos are ink on nothing - a black wordmark on a transparent
 * PNG. That reads beautifully on the light sidebar and disappears entirely
 * on the dark one, which is why there is a second slot. The dark variant is
 * optional: an organisation whose mark carries its own background looks
 * right either way and leaves it empty, so every lookup falls back to the
 * light one rather than to nothing.
 *
 * Keep both apps resolving through this shape. The web sidebar, the admin
 * console's org rows and the mobile show picker all pick a logo, and picking
 * it three different ways is how one of them ends up showing the wrong one.
 */
export interface OrgBrandingFields {
  profileImageUrl?: string;
  profileImageUrlDark?: string;
  appIconUrl?: string;
  appIconUrlDark?: string;
}

const pick = (dark: string | undefined, light: string | undefined, isDark: boolean): string =>
  ((isDark && dark) || light || '').trim();

/** The wide wordmark, for the sidebar header. */
export const resolveOrgLogo = (org: OrgBrandingFields | undefined | null, isDark: boolean): string =>
  pick(org?.profileImageUrlDark, org?.profileImageUrl, isDark);

/**
 * The square mark, for the collapsed rail and anywhere a logo has to fit a
 * square. Falls back to the wordmark before giving up, which is what the
 * collapsed sidebar has always done.
 */
export const resolveOrgIcon = (org: OrgBrandingFields | undefined | null, isDark: boolean): string =>
  pick(org?.appIconUrlDark, org?.appIconUrl, isDark) || resolveOrgLogo(org, isDark);
